import { useMemo, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { formatHours } from "@/lib/employeeTimes";
import {
  createDefaultModelDays,
  normalizeModelDay,
  WEEKDAY_LABELS,
  type WorkTimeModel,
  type WorkTimeModelDay,
} from "@/lib/workTimeModels";

interface ModelEditorProps {
  model: WorkTimeModel | null;
  isNew?: boolean;
  saving?: boolean;
  onSave: (payload: { name: string; days: WorkTimeModelDay[] }) => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancel?: () => void;
}

export function ModelEditor({
  model,
  isNew,
  saving,
  onSave,
  onDelete,
  onCancel,
}: ModelEditorProps) {
  const [name, setName] = useState(model?.name ?? "");
  const [days, setDays] = useState<WorkTimeModelDay[]>(
    model?.days?.map((d) => normalizeModelDay(d)) ?? createDefaultModelDays(),
  );
  const [error, setError] = useState<string | null>(null);

  const weeklyHours = useMemo(
    () => days.reduce((sum, d) => sum + Number(d.hours_decimal || 0), 0),
    [days],
  );
  const workdayCount = useMemo(
    () => days.filter((d) => d.is_workday).length,
    [days],
  );
  const averageDailySoll = workdayCount > 0 ? weeklyHours / workdayCount : null;

  const updateDay = (weekday: number, patch: Partial<WorkTimeModelDay>) => {
    setDays((prev) =>
      prev.map((d) => (d.weekday === weekday ? normalizeModelDay({ ...d, ...patch }) : d)),
    );
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Bitte einen Namen eingeben.");
      return;
    }
    setError(null);
    try {
      await onSave({ name: trimmed, days });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    }
  };

  if (!model && !isNew) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center rounded-ios border border-dashed border-separator bg-card p-8 text-sm text-label-secondary">
        Modell links auswählen oder neues Modell anlegen.
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-ios border border-separator bg-card p-5 shadow-ios">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-label">
            {isNew ? "Neues Arbeitszeitmodell" : "Modell bearbeiten"}
          </h3>
          {model?.is_default && (
            <p className="mt-1 text-xs text-label-secondary">
              Standardmodell — gilt für alle Mitarbeiter ohne eigene Zuweisung.
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs text-label-secondary">Wochenstunden (netto)</div>
          <div className="text-lg font-semibold text-accent">{formatHours(weeklyHours)}</div>
          <div className="mt-1 text-xs text-label-secondary">Soll / Arbeitstag</div>
          <div className="text-sm font-semibold tabular-nums text-label">
            {averageDailySoll != null ? formatHours(averageDailySoll) : "—"}
          </div>
        </div>
      </div>

      <TextInput
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="z. B. Teilzeit, Schichtarbeit"
      />

      <p className="text-xs text-label-secondary">
        Netto je Tag = geplante Zeit zur Vorbelegung. Überstunden, Urlaub und Feiertage nutzen
        das mittlere Tagessoll (Wochenstunden ÷ Arbeitstage).
      </p>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-separator text-left text-xs text-label-secondary">
              <th className="py-2 pr-3">Tag</th>
              <th className="py-2 pr-3">Arbeitstag</th>
              <th className="py-2 pr-3">Von</th>
              <th className="py-2 pr-3">Bis</th>
              <th className="py-2 pr-3">Pause von</th>
              <th className="py-2 pr-3">Pause bis</th>
              <th className="py-2 text-right">Netto</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => (
              <tr key={day.weekday} className="border-b border-separator/60">
                <td className="py-2 pr-3 font-medium text-label">
                  {WEEKDAY_LABELS[day.weekday]}
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="checkbox"
                    checked={day.is_workday}
                    onChange={(e) =>
                      updateDay(day.weekday, {
                        is_workday: e.target.checked,
                        time_from: e.target.checked ? "08:00" : null,
                        time_to: e.target.checked ? "16:30" : null,
                        break_from: e.target.checked ? "12:00" : null,
                        break_to: e.target.checked ? "12:30" : null,
                      })
                    }
                    className="h-4 w-4 rounded border-separator text-accent"
                  />
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="time"
                    disabled={!day.is_workday}
                    value={day.time_from?.slice(0, 5) || ""}
                    onChange={(e) => updateDay(day.weekday, { time_from: e.target.value })}
                    className={clsx(
                      "eh-input w-full",
                      !day.is_workday && "opacity-40",
                    )}
                  />
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="time"
                    disabled={!day.is_workday}
                    value={day.time_to?.slice(0, 5) || ""}
                    onChange={(e) => updateDay(day.weekday, { time_to: e.target.value })}
                    className={clsx(
                      "eh-input w-full",
                      !day.is_workday && "opacity-40",
                    )}
                  />
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="time"
                    disabled={!day.is_workday}
                    value={day.break_from?.slice(0, 5) || ""}
                    onChange={(e) =>
                      updateDay(day.weekday, {
                        break_from: e.target.value || null,
                      })
                    }
                    className={clsx(
                      "eh-input w-full",
                      !day.is_workday && "opacity-40",
                    )}
                  />
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="time"
                    disabled={!day.is_workday}
                    value={day.break_to?.slice(0, 5) || ""}
                    onChange={(e) =>
                      updateDay(day.weekday, {
                        break_to: e.target.value || null,
                      })
                    }
                    className={clsx(
                      "eh-input w-full",
                      !day.is_workday && "opacity-40",
                    )}
                  />
                </td>
                <td className="py-2 text-right tabular-nums font-medium text-label">
                  {day.is_workday ? formatHours(day.hours_decimal) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <Alert variant="error" title="Fehler">{error}</Alert>}

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
          {saving ? "Speichern…" : "Speichern"}
        </Button>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            Abbrechen
          </Button>
        )}
        {onDelete && !model?.is_default && (
          <Button variant="danger" onClick={() => void onDelete()} disabled={saving}>
            Löschen
          </Button>
        )}
      </div>
    </div>
  );
}
